import type { ExhibitionLocalization, MuseumContent } from "./contracts";
import type { RegionId } from "./regions";

export type Language = "en" | "ar" | "zh" | "hi" | "es";

/**
 * Menu order, and the order the picker renders. English leads because it is the base
 * language of the exhibition content itself; Arabic follows because these are Saudi
 * sites and Arabic is the language of their inscriptions and their audience.
 */
export const LANGUAGES: readonly Language[] = ["en", "ar", "zh", "hi", "es"];

/**
 * The studio name, deliberately not translated and deliberately not part of UiCopy: a
 * brand is the one string on the page that must read identically in every language.
 */
export const BRAND = "Simam Digital";

/** Endonyms, so a reader can find their own language without reading English first. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  ar: "العربية",
  zh: "中文",
  hi: "हिन्दी",
  es: "Español",
};

/** Narrow arbitrary storage or form input to a supported language. */
export function parseLanguage(value: string | null): Language {
  return LANGUAGES.includes(value as Language) ? (value as Language) : "en";
}

/**
 * The picker markup, shared by the index and the in-museum toolbar so a language can
 * never appear in one and not the other. Names are static literals, so there is nothing
 * to escape.
 */
export function languageOptionsHtml(selected: Language): string {
  return LANGUAGES.map(
    (language) =>
      `<option value="${language}"${language === selected ? " selected" : ""}>` +
      `${LANGUAGE_NAMES[language]}</option>`,
  ).join("");
}

/** The four lines a heritage opens on, before any of its material is shown. */
/**
 * How a heritage ends.
 *
 * `note` is not optional decoration. A coda plate is the one image in a heritage that
 * need not descend from anything real, and the Greek one does not: it is a model's
 * guess at a building nobody photographed whole. The note is where the page says so.
 */
export type RegionCoda = {
  line: string;
  note: string;
};

export type RegionOpening = {
  title: string;
  lede: string;
  orbitTitle: string;
  orbitLede: string;
};

export type UiCopy = {
  locale: string;
  direction: "ltr" | "rtl";
  languageLabel: string;
  regionLabel: string;
  /** Marks a heritage that is named but not yet walkable. */
  regionSoon: string;
  /**
   * In the reader's own language, not the endonym the language picker uses. A visitor
   * reading Chinese is choosing between subjects, not looking for their own tongue.
   */
  regionNames: Record<RegionId, string>;
  collectionEyebrow: string;
  collectionTitle: string;
  collectionIntro: string;
  collectionCreditLead: string;
  collectionCreditOne: string;
  collectionPlatesTitle: string;
  collectionObjectsTitle: string;
  collectionObjectsIntro: string;
  collectionGenerated: string;
  collectionTurn: string;
  showcaseRoomsGenerated: string;
  /**
   * The opening, per heritage. Absent means the region opens on the original
   * `opening*` and `orbit*` lines, which are Saudi Arabia's — so a new region gets
   * its own words by adding an entry, and never by accidentally inheriting a
   * headline about the Nabataean south.
   */
  regionOpenings: Partial<Record<RegionId, RegionOpening>>;
  regionCodas: Partial<Record<RegionId, RegionCoda>>;
  pageDescription: string;
  canvasLabel: string;
  museumEyebrow: string;
  fallbackTitle: string;
  loadingThesis: string;
  preparing: string;
  exhibitionsTitle: string;
  exhibitionsIntro: string;
  storyEyebrow: string;
  storyLead: string;
  storyScrollCue: string;
  storyIndexTitle: string;
  storyChapterLabel: (index: number, total: number) => string;
  openingTitle: string;
  openingLede: string;
  orbitTitle: string;
  orbitLede: string;
  interludeSky: string;
  interludeStone: string;
  codaLine: string;
  motionLabel: string;
  motionOn: string;
  motionOff: string;
  motionHint: string;
  ambienceLabel: string;
  ambienceOn: string;
  ambienceOff: string;
  ambienceHint: string;
  footerTagline: string;
  footerCredits: string;
  footerAttribution: string;
  footerAttributionCollection: string;
  creditsCollection: string;
  creditsTitle: string;
  creditsPhotography: string;
  creditsReconstructions: string;
  creditsTypefaces: string;
  creditsClose: string;
  revealThen: string;
  revealNow: string;
  revealCaption: string;
  revealSliderLabel: string;
  revealValueText: (position: number) => string;
  revealPhotographAlt: string;
  revealReconstructionAlt: string;
  openExhibition: string;
  /** The open-air walkthrough, still a preview. See `src/museum/`. */
  walkOpenAir: string;
  exhibitionCardMeta: (roomCount: number, artworkCount: number) => string;
  emptyExhibitions: string;
  backToExhibitions: string;
  notFoundTitle: string;
  notFoundMessage: string;
  enter: string;
  routeAndWorks: string;
  howToVisit: string;
  pauseEyebrow: string;
  pauseTitle: string;
  pauseMessage: string;
  continueVisit: string;
  fatalEyebrow: string;
  fatalTitle: string;
  retry: string;
  consultRoute: string;
  previousArtwork: string;
  nextArtwork: string;
  works: string;
  home: string;
  help: string;
  visitControls: string;
  joystickLabel: string;
  viewArtwork: string;
  mapTitle: string;
  mapLabel: string;
  presetLabel: string;
  presetPlaceholder: string;
  presetHint: string;
  closeArtwork: string;
  routeEyebrow: string;
  routeTitle: string;
  closeRoute: string;
  routeIntro: string;
  helpTitle: string;
  closeHelp: string;
  desktopTitle: string;
  desktopHelp: string;
  touchTitle: string;
  touchHelp: string;
  tourTitle: string;
  tourHelp: string;
  playTour: string;
  stopTour: string;
  tourStopLabel: (index: number, total: number) => string;
  tourStarted: string;
  tourFinished: string;
  showcaseEyebrow: string;
  showcaseTitle: string;
  showcaseTitleGenerated: string;
  showcaseIntroGenerated: string;
  showcaseIntro: string;
  showcaseOpen: string;
  showcaseLoading: string;
  showcaseOwnCredit: string;
  showcaseSampleNotice: string;
  showcaseCreditLink: string;
  showcaseArchiveTitle: string;
  author: string;
  century: string;
  styles: string;
  deities: string;
  mortals: string;
  museum: string;
  ready: (rooms: number, artworks: number) => string;
  corridor: string;
  routeCompleted: string;
  loadPhases: Record<"manifest" | "report" | "content" | "scene" | "validated", string>;
  artworkKicker: (order: number, total: number, room: string) => string;
  artworkImageLabel: (title: string, author: string) => string;
  viewFullscreen: (title: string, author: string) => string;
  exitFullscreen: (title: string) => string;
  towards: (title: string) => string;
  towardsPassage: string;
  selection: (title: string) => string;
  visitStarted: (title: string) => string;
  roomChanged: (room: string) => string;
  activeArtwork: (order: number, total: number, title: string) => string;
  completedWith: (title: string) => string;
  navigationPosition: (title: string) => string;
  resetAnnouncement: string;
  pointerLockFailure: string;
  invalidLink: string;
  unknownArtwork: string;
  defaultPause: string;
  webglFailure: string;
  contextLoss: string;
  identityFailure: string;
  missingAsset: string;
  genericFailure: string;
};

const STORAGE_KEY = "simam-heritage-language";

export function readLanguage(storage: Storage = window.localStorage): Language {
  try {
    return parseLanguage(storage.getItem(STORAGE_KEY));
  } catch {
    return "en";
  }
}

export function storeLanguage(language: Language, storage: Storage = window.localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, language);
  } catch {
    // Restricted storage must never prevent a visit.
  }
}

export function copyFor(language: Language): UiCopy {
  return COPY[language];
}

export function localizeContent(
  content: MuseumContent,
  localization: ExhibitionLocalization | null,
): MuseumContent {
  if (localization === null) return content;
  const rooms = new Map(localization.rooms.map((room) => [room.id, room]));
  const artworks = new Map(localization.artworks.map((artwork) => [artwork.id, artwork]));
  return {
    ...content,
    title: localization.title,
    thesis: localization.thesis,
    rooms: content.rooms.map((room) => ({ ...room, title: rooms.get(room.id)?.title ?? room.title })),
    artworks: content.artworks.map((artwork) => ({
      ...artwork,
      ...(artworks.get(artwork.id) ?? {}),
    })),
  };
}

/** Arabic counted nouns agree by number: one, two, few (3-10) and many (11+). */
type ArabicNoun = { one: string; two: string; few: string; many: string };

const ROOM_NOUN: ArabicNoun = {
  one: "قاعة واحدة",
  two: "قاعتان",
  few: "قاعات",
  many: "قاعة",
};

const WORK_NOUN: ArabicNoun = {
  one: "عمل واحد",
  two: "عملان",
  few: "أعمال",
  many: "عملًا",
};

function arabicCount(count: number, noun: ArabicNoun): string {
  if (count === 1) return noun.one;
  if (count === 2) return noun.two;
  // Beyond ten the noun reverts to the singular, so only 3-10 takes the plural.
  return `${count} ${count >= 3 && count <= 10 ? noun.few : noun.many}`;
}

const COPY: Record<Language, UiCopy> = {
  es: {
    locale: "es",
    direction: "ltr",
    languageLabel: "Idioma",
    regionLabel: "Patrimonio",
    regionSoon: "Próximamente",
    regionNames: {
      "saudi-arabia": "Arabia Saudí",
      "united-arab-emirates": "Emiratos Árabes Unidos",
      italy: "Italia",
      greece: "Grecia",
      "united-kingdom": "Reino Unido",
      ireland: "Irlanda",
      france: "Francia",
    },
    pageDescription: "Exposiciones virtuales creadas a partir de un catálogo de arte.",
    canvasLabel: "Vista tridimensional del museo. El recorrido también está disponible como lista.",
    museumEyebrow: "Museo virtual",
    fallbackTitle: "Museo virtual",
    loadingThesis: "Cargando relato curatorial…",
    preparing: "Preparando los contratos del museo…",
    exhibitionsTitle: "Exposiciones",
    exhibitionsIntro: "Elige una exposición para comenzar la visita.",
    storyEyebrow: "Patrimonio de Arabia Saudí",
    storyLead:
      "Cinco lugares, del sur nabateo a las montañas de Asir. Cada uno reconstruido " +
      "como una galería que se recorre a pie, con fotografía documental y textos en " +
      "árabe e inglés.",
    storyScrollCue: "Desplázate para recorrerlos",
    storyIndexTitle: "Las cinco exposiciones",
    storyChapterLabel: (index, total) => `${index} de ${total}`,
        openingTitle: "Patrimonio de Arabia Saudí",
    openingLede:
      "Cinco lugares inscritos por la UNESCO, reconstruidos como galerías que se recorren a pie.",
    orbitTitle: "Una península, cinco lugares",
    orbitLede:
      "Desde las tumbas nabateas del noroeste hasta los pueblos de piedra de las montañas de Asir.",
        interludeSky: "El mismo cielo sobre todos ellos",
    interludeStone: "Todo aquí fue tallado del suelo que lo sostiene",
        codaLine: "Y todo ello sigue en pie bajo la ciudad que vino después",
        motionLabel: "Movimiento",
    motionOn: "Movimiento activado",
    motionOff: "Movimiento desactivado",
    motionHint: "Vídeo y desplazamiento animado",
    ambienceLabel: "Sonido ambiente",
    ambienceOn: "Sonido activado",
    ambienceOff: "Sonido desactivado",
    ambienceHint: "Viento y resonancia de piedra, sintetizados",
    footerTagline: "Exposiciones tridimensionales del patrimonio cultural, construidas de forma determinista.",
    footerCredits: "Créditos y licencias",
    footerAttribution:
      "Fotografía de Wikimedia Commons bajo licencias Creative Commons. La atribución " +
      "viaja con las imágenes.",
    footerAttributionCollection:
      "Cada fotografía se acredita a su autor allí donde aparece, y también todo lo " +
      "generado a partir de ella.",
    creditsCollection:
      "Las fotografías de esta página, y cada pieza tridimensional generada a partir de " +
      "ellas:",
    creditsTitle: "Créditos y licencias",
    creditsPhotography:
      "Cada fotografía procede de Wikimedia Commons. Se indican el autor y la licencia " +
      "concreta de cada una.",
    creditsReconstructions:
      "Las reconstrucciones históricas se generaron a partir de estas mismas fotografías, " +
      "desde el mismo punto de vista. Son interpretaciones y aparecen siempre etiquetadas.",
    creditsTypefaces: "Tipografías, bajo SIL Open Font License 1.1:",
    creditsClose: "Cerrar los créditos",
    revealThen: "Reconstrucción",
    revealNow: "Fotografía",
    revealCaption:
      "Reconstrucción generada a partir de la fotografía documental, desde el mismo punto " +
      "de vista. Es una interpretación, no un registro.",
    revealSliderLabel: "Comparar la reconstrucción con la fotografía",
    revealValueText: (position) =>
      position >= 50
        ? `Reconstrucción ${position}% visible`
        : `Fotografía ${100 - position}% visible`,
    revealPhotographAlt: "Fotografía documental del lugar tal como está hoy",
    revealReconstructionAlt: "Reconstrucción del mismo lugar en su estado histórico",
    openExhibition: "Abrir exposición",
    walkOpenAir: "Pasear al aire libre (avance)",
    exhibitionCardMeta: (roomCount, artworkCount) =>
      `${roomCount} ${roomCount === 1 ? "sala" : "salas"} · ${artworkCount} ${artworkCount === 1 ? "obra" : "obras"}`,
    emptyExhibitions: "Todavía no hay exposiciones publicadas.",
    backToExhibitions: "Volver a exposiciones",
    notFoundTitle: "Exposición no encontrada",
    notFoundMessage: "La exposición solicitada no existe o no está publicada.",
    enter: "Entrar al museo",
    routeAndWorks: "Recorrido y obras",
    howToVisit: "Cómo visitar",
    pauseEyebrow: "Visita en pausa",
    pauseTitle: "Continúa cuando quieras",
    pauseMessage: "El movimiento está detenido. Tu posición no ha cambiado.",
    continueVisit: "Continuar visita",
    fatalEyebrow: "No se pudo abrir el museo",
    fatalTitle: "Error de carga",
    retry: "Reintentar",
    consultRoute: "Consultar recorrido",
    previousArtwork: "Obra anterior",
    nextArtwork: "Obra siguiente",
    works: "Obras",
    home: "Inicio",
    help: "Ayuda",
    visitControls: "Controles de la visita",
    joystickLabel: "Control táctil de movimiento",
    viewArtwork: "Ver obra",
    mapTitle: "Plano del museo",
    mapLabel: "Plano de las salas, la posición del visitante y las obras",
    presetLabel: "Ir directamente a una obra",
    presetPlaceholder: "Ir a una obra…",
    presetHint: "También puedes usar las teclas numéricas disponibles",
    closeArtwork: "Cerrar información de la obra",
    routeEyebrow: "Recorrido curatorial",
    routeTitle: "Obras de la exposición",
    closeRoute: "Cerrar recorrido",
    routeIntro: "Abre cualquier obra en formato accesible. Tu posición no cambiará.",
    helpTitle: "Controles",
    closeHelp: "Cerrar ayuda",
    desktopTitle: "Escritorio",
    desktopHelp: "WASD o flechas para caminar; ratón para mirar; apunta a una obra y pulsa E.",
    touchTitle: "Pantalla táctil",
    touchHelp: "Usa el control izquierdo para caminar y arrastra sobre la vista para mirar.",
    tourTitle: "Recorrido",
    tourHelp: "La flecha señala la obra activa. Usa Anterior, Siguiente u Obras.",
    playTour: "Recorrido guiado",
    stopTour: "Salir del recorrido",
    tourStopLabel: (index, total) => `Parada ${index} de ${total}`,
    tourStarted: "Recorrido guiado iniciado. Muévete para tomar el control.",
    tourFinished: "El recorrido guiado ha terminado. Sigue explorando por tu cuenta.",
    showcaseEyebrow: "Salas transitables",
    regionCodas: {
      greece: {
        line: "Lo que hubo aqu\u00ed, conjeturado",
        note:
          "El templo se recompone porque se le pidi\u00f3 a un modelo que lo imaginara, no " +
        "porque alguien lo registrara. Todo lo dem\u00e1s en esta p\u00e1gina parte de una " +
        "fotograf\u00eda. Esta es la \u00fanica imagen que parte de nada.",
      },
    },
    regionOpenings: {
      greece: {
        title: "Patrimonio griego",
        lede: "Dos ciudades, fotografiadas por un visitante, y reconstruidas a partir de esas fotografías.",
        orbitTitle: "Tesalónica y Atenas",
        orbitLede: "Una rotonda romana en el norte, la Acrópolis en el sur, y todo un museo de objetos entre medias.",
      },
    },
    collectionEyebrow: "Tesalónica y Atenas",
    collectionTitle: "Grecia, en tres pasadas",
    collectionIntro: "Diecisiete fotografías, y todo lo tridimensional de esta página generado a partir de ellas. Nada aquí es un escaneo de un sitio, y nada es nuestro.",
    collectionCreditLead: "Fotografías de",
    collectionCreditOne: "Fotografía de",
    collectionPlatesTitle: "Las fotografías",
    collectionObjectsTitle: "Los objetos, girados",
    collectionObjectsIntro: "Cada uno se generó a partir de la única fotografía que tiene al lado. Arrástralo para girarlo, y compara las dos mitades.",
    collectionGenerated: "Generado a partir de una fotografía de",
    collectionTurn: "Gíralo",
    showcaseRoomsGenerated: "Generado a partir de fotografías de",
    showcaseTitle: "Salas escaneadas",
    showcaseTitleGenerated: "Salas reconstruidas a partir de fotografías",
    showcaseIntroGenerated:
      "Splats gaussianos generados a partir de las fotografías anteriores, transitables " +
      "en el navegador. Cada sala se abre al llegar a ella: arrastra para mirar, W A S D " +
      "para moverte.",
    showcaseIntro:
      "Espacios reales capturados como splats gaussianos y recorribles en el navegador. Cada sala se abre al llegar a ella: arrastra para mirar, W A S D para moverte.",
    showcaseOpen: "Entrar en la sala",
    showcaseLoading: "Abriendo la vista 3D…",
    showcaseOwnCredit: "Simam Digital",
    showcaseSampleNotice:
      "Estas capturas son de terceros y se muestran solo para ilustrar el formato. La versión final usaría un escaneo del recinto del propio museo.",
    showcaseCreditLink: "Autoría indicada en el visor",
    showcaseArchiveTitle: "Salas construidas a medida",
    author: "Autoría",
    century: "Siglo",
    styles: "Estilos",
    deities: "Deidades",
    mortals: "Mortales",
    museum: "Museo",
    ready: (rooms, artworks) => `Museo preparado: ${rooms} sala(s) y ${artworks} obra(s).`,
    corridor: "Pasaje entre salas",
    routeCompleted: "Recorrido completado",
    loadPhases: {
      manifest: "Validando el manifiesto…",
      report: "Comprobando el informe de Blender…",
      content: "Cargando el recorrido y las reseñas…",
      scene: "Verificando la escena tridimensional…",
      validated: "Preparando cámara, colisiones e interacción…",
    },
    artworkKicker: (order, total, room) => `Obra ${order} de ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}, de ${author}`,
    viewFullscreen: (title, author) => `Ver ${title}, de ${author}, a pantalla completa`,
    exitFullscreen: (title) => `Salir de la vista a pantalla completa de ${title}`,
    towards: (title) => `Hacia ${title}`,
    towardsPassage: "Hacia el pasaje",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `Visita iniciada. Obra activa: ${title}.`,
    roomChanged: (room) => `Espacio actual: ${room}.`,
    activeArtwork: (order, total, title) => `Obra activa ${order} de ${total}: ${title}.`,
    completedWith: (title) => `Recorrido completado con ${title}.`,
    navigationPosition: (title) => `Posición de visita: ${title}. La obra está frente a ti.`,
    resetAnnouncement: "Has vuelto a la entrada del museo.",
    pointerLockFailure: "El navegador no concedió el control del ratón. Inténtalo de nuevo.",
    invalidLink: "El enlace de obra no era válido y se ha descartado.",
    unknownArtwork: "La obra indicada no pertenece a esta exposición.",
    defaultPause: "El movimiento está detenido. Tu posición no ha cambiado.",
    webglFailure: "Este navegador no ofrece WebGL2. La visita 3D requiere un navegador compatible.",
    contextLoss: "El navegador perdió el contexto gráfico. Recarga para reconstruir la visita.",
    identityFailure: "Los archivos publicados no pertenecen a la misma construcción.",
    missingAsset: "Falta uno de los archivos generados. Publica de nuevo los recursos.",
    genericFailure: "La escena no ha podido validarse completamente.",
  },
  en: {
    locale: "en",
    direction: "ltr",
    languageLabel: "Language",
    regionLabel: "Heritage",
    regionSoon: "In preparation",
    regionNames: {
      "saudi-arabia": "Saudi Arabia",
      "united-arab-emirates": "United Arab Emirates",
      italy: "Italy",
      greece: "Greece",
      "united-kingdom": "United Kingdom",
      ireland: "Ireland",
      france: "France",
    },
    pageDescription: "Virtual exhibitions built from an art catalogue.",
    canvasLabel: "Three-dimensional museum view. The tour is also available as a list.",
    museumEyebrow: "Virtual museum",
    fallbackTitle: "Virtual museum",
    loadingThesis: "Loading the curatorial narrative…",
    preparing: "Preparing the museum contracts…",
    exhibitionsTitle: "Exhibitions",
    exhibitionsIntro: "Choose an exhibition to begin your visit.",
    storyEyebrow: "Saudi Arabian heritage",
    storyLead:
      "Five places, from the Nabataean south to the mountains of Asir. Each rebuilt " +
      "as a gallery you walk through, with documentary photography and writing in " +
      "Arabic and English.",
    storyScrollCue: "Scroll to walk through them",
    storyIndexTitle: "All five exhibitions",
    storyChapterLabel: (index, total) => `${index} of ${total}`,
        openingTitle: "Saudi Arabian heritage",
    openingLede:
      "Five UNESCO-listed places, rebuilt as galleries you can walk through.",
    orbitTitle: "One peninsula, five places",
    orbitLede:
      "From the Nabataean tombs of the north-west to the stone villages of the Asir highlands.",
        interludeSky: "The same sky over all of them",
    interludeStone: "Everything here was cut from the ground it stands on",
        codaLine: "All of it still standing under the country that grew around it",
        motionLabel: "Motion",
    motionOn: "Motion on",
    motionOff: "Motion off",
    motionHint: "Video and animated scrolling",
    ambienceLabel: "Ambient sound",
    ambienceOn: "Sound on",
    ambienceOff: "Sound off",
    ambienceHint: "Wind and stone resonance, synthesised",
    footerTagline: "Three-dimensional exhibitions of cultural heritage, built deterministically.",
    footerCredits: "Credits and licences",
    footerAttribution:
      "Photography from Wikimedia Commons under Creative Commons licences. Attribution " +
      "travels with the images.",
    footerAttributionCollection:
      "Photographs are credited to their photographer wherever they appear, and so is " +
      "everything generated from them.",
    creditsCollection:
      "The photographs on this page, and every three-dimensional piece generated from " +
      "them:",
    creditsTitle: "Credits and licences",
    creditsPhotography:
      "Every photograph comes from Wikimedia Commons. Each is listed with its " +
      "photographer and its specific licence.",
    creditsReconstructions:
      "Historical reconstructions were generated from these same photographs, from the " +
      "same viewpoint. They are interpretations, and are labelled as such wherever shown.",
    creditsTypefaces: "Typefaces, under SIL Open Font License 1.1:",
    creditsClose: "Close the credits",
    revealThen: "Reconstruction",
    revealNow: "Photograph",
    revealCaption:
      "Reconstruction generated from the documentary photograph, from the same viewpoint. " +
      "It is an interpretation, not a record.",
    revealSliderLabel: "Compare the reconstruction with the photograph",
    revealValueText: (position) =>
      position >= 50
        ? `Reconstruction ${position}% visible`
        : `Photograph ${100 - position}% visible`,
    revealPhotographAlt: "Documentary photograph of the site as it stands today",
    revealReconstructionAlt: "Reconstruction of the same view in its historical state",
    openExhibition: "Open exhibition",
    walkOpenAir: "Walk it in the open air (preview)",
    exhibitionCardMeta: (roomCount, artworkCount) =>
      `${roomCount} ${roomCount === 1 ? "room" : "rooms"} · ${artworkCount} ${artworkCount === 1 ? "work" : "works"}`,
    emptyExhibitions: "There are no published exhibitions yet.",
    backToExhibitions: "Back to exhibitions",
    notFoundTitle: "Exhibition not found",
    notFoundMessage: "The requested exhibition does not exist or is not published.",
    enter: "Enter the museum",
    routeAndWorks: "Tour and artworks",
    howToVisit: "How to visit",
    pauseEyebrow: "Visit paused",
    pauseTitle: "Continue whenever you are ready",
    pauseMessage: "Movement is paused. Your position has not changed.",
    continueVisit: "Continue visit",
    fatalEyebrow: "The museum could not be opened",
    fatalTitle: "Loading error",
    retry: "Try again",
    consultRoute: "View the tour",
    previousArtwork: "Previous artwork",
    nextArtwork: "Next artwork",
    works: "Artworks",
    home: "Home",
    help: "Help",
    visitControls: "Visit controls",
    joystickLabel: "Touch movement control",
    viewArtwork: "View artwork",
    mapTitle: "Museum map",
    mapLabel: "Map of the galleries, visitor position, and artworks",
    presetLabel: "Go directly to an artwork",
    presetPlaceholder: "Go to an artwork…",
    presetHint: "You can also use the available number keys",
    closeArtwork: "Close artwork information",
    routeEyebrow: "Curatorial tour",
    routeTitle: "Exhibition artworks",
    closeRoute: "Close tour",
    routeIntro: "Open any artwork in an accessible format. Your position will not change.",
    helpTitle: "Controls",
    closeHelp: "Close help",
    desktopTitle: "Desktop",
    desktopHelp: "Use WASD or arrows to walk, the mouse to look, and E to inspect an artwork.",
    touchTitle: "Touchscreen",
    touchHelp: "Use the left control to walk and drag across the view to look around.",
    tourTitle: "Tour",
    tourHelp: "The arrow points to the active artwork. Use Previous, Next, or Artworks.",
    playTour: "Guided tour",
    stopTour: "Leave tour",
    tourStopLabel: (index, total) => `Stop ${index} of ${total}`,
    tourStarted: "Guided tour started. Move to take back control.",
    tourFinished: "The guided tour has ended. Carry on exploring on your own.",
    showcaseEyebrow: "Rooms you can walk",
    regionCodas: {
      greece: {
        line: "What stood here, guessed at",
        note:
          "The temple closes itself because a model was asked to imagine it, not because " +
        "anyone recorded it. Everything else on this page starts with a photograph. " +
        "This is the one image that starts with nothing.",
      },
    },
    regionOpenings: {
      greece: {
        title: "Greek heritage",
        lede: "Two cities, photographed by one visitor, and rebuilt from those photographs.",
        orbitTitle: "Thessaloniki and Athens",
        orbitLede: "A Roman rotunda in the north, the Acropolis in the south, and a museum's worth of objects in between.",
      },
    },
    collectionEyebrow: "Thessaloniki and Athens",
    collectionTitle: "Greece, in three passes",
    collectionIntro: "Seventeen photographs, and everything three-dimensional on this page generated from them. None of it is a scan of a site, and none of it is ours.",
    collectionCreditLead: "Photographs by",
    collectionCreditOne: "Photograph by",
    collectionPlatesTitle: "The photographs",
    collectionObjectsTitle: "The objects, turned",
    collectionObjectsIntro: "Each of these was generated from the single photograph beside it. Drag to turn one, and compare the halves — where they disagree is where the model was guessing.",
    collectionGenerated: "Generated from a photograph by",
    collectionTurn: "Turn it",
    showcaseRoomsGenerated: "Generated from photographs by",
    showcaseTitle: "Scanned rooms",
    showcaseTitleGenerated: "Rooms rebuilt from photographs",
    showcaseIntroGenerated:
      "Gaussian splats generated from the photographs above, and walkable in the " +
      "browser. Each one opens as you reach it — drag to look, W A S D to move.",
    showcaseIntro:
      "Real spaces captured as Gaussian splats and walkable in the browser. Each one opens as you reach it — drag to look, W A S D to move.",
    showcaseOpen: "Step inside",
    showcaseLoading: "Opening the 3D view…",
    showcaseOwnCredit: "Simam Digital",
    showcaseSampleNotice:
      "These captures are other people's work, shown only to demonstrate the format. The finished piece would use a scan of the museum's own site.",
    showcaseCreditLink: "Creator credited in the viewer",
    showcaseArchiveTitle: "Purpose-built rooms",
    author: "Artist",
    century: "Century",
    styles: "Styles",
    deities: "Deities",
    mortals: "Mortals",
    museum: "Museum",
    ready: (rooms, artworks) => `Museum ready: ${rooms} room(s) and ${artworks} artwork(s).`,
    corridor: "Passage between galleries",
    routeCompleted: "Tour completed",
    loadPhases: {
      manifest: "Validating the manifest…",
      report: "Checking the Blender report…",
      content: "Loading the tour and essays…",
      scene: "Verifying the three-dimensional scene…",
      validated: "Preparing camera, collisions, and interaction…",
    },
    artworkKicker: (order, total, room) => `Artwork ${order} of ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}, by ${author}`,
    viewFullscreen: (title, author) => `View ${title}, by ${author}, full screen`,
    exitFullscreen: (title) => `Exit the full-screen view of ${title}`,
    towards: (title) => `Towards ${title}`,
    towardsPassage: "Towards the passage",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `Visit started. Active artwork: ${title}.`,
    roomChanged: (room) => `Current space: ${room}.`,
    activeArtwork: (order, total, title) => `Active artwork ${order} of ${total}: ${title}.`,
    completedWith: (title) => `Tour completed with ${title}.`,
    navigationPosition: (title) => `Viewing position: ${title}. The artwork is in front of you.`,
    resetAnnouncement: "You have returned to the museum entrance.",
    pointerLockFailure: "The browser did not grant mouse control. Try again.",
    invalidLink: "The artwork link was invalid and has been discarded.",
    unknownArtwork: "The requested artwork does not belong to this exhibition.",
    defaultPause: "Movement is paused. Your position has not changed.",
    webglFailure: "This browser does not support WebGL2. The 3D visit requires a compatible browser.",
    contextLoss: "The browser lost its graphics context. Reload to reconstruct the visit.",
    identityFailure: "The published files do not belong to the same build.",
    missingAsset: "A generated file is missing. Publish the assets again.",
    genericFailure: "The scene could not be fully validated.",
  },
  ar: {
    locale: "ar",
    direction: "rtl",
    languageLabel: "اللغة",
    regionLabel: "التراث",
    regionSoon: "قيد الإعداد",
    regionNames: {
      "saudi-arabia": "المملكة العربية السعودية",
      "united-arab-emirates": "الإمارات العربية المتحدة",
      italy: "إيطاليا",
      greece: "اليونان",
      "united-kingdom": "المملكة المتحدة",
      ireland: "أيرلندا",
      france: "فرنسا",
    },
    pageDescription: "معارض افتراضية مبنية من فهرس فني.",
    canvasLabel: "عرض ثلاثي الأبعاد للمتحف. الجولة متاحة أيضًا في صورة قائمة.",
    museumEyebrow: "متحف افتراضي",
    fallbackTitle: "متحف افتراضي",
    loadingThesis: "جارٍ تحميل السرد القيمي…",
    preparing: "جارٍ تجهيز عقود المتحف…",
    exhibitionsTitle: "المعارض",
    exhibitionsIntro: "اختر معرضًا لبدء زيارتك.",
    storyEyebrow: "التراث السعودي",
    storyLead:
      "خمسة مواقع، من الجنوب النبطي إلى جبال عسير. أُعيد بناء كل موقع قاعةً تُزار " +
      "سيرًا على الأقدام، بتصوير وثائقي ونصوص بالعربية والإنجليزية.",
    storyScrollCue: "مرِّر لتتجوّل فيها",
    storyIndexTitle: "المعارض الخمسة",
    // Arabic ordinals disagree with the noun, so the plain cardinal pair is used.
    storyChapterLabel: (index, total) => `${index} من ${total}`,
        openingTitle: "التراث السعودي",
    openingLede: "خمسة مواقع مسجّلة في اليونسكو، أُعيد بناؤها كصالات يمكن التجوّل فيها.",
    orbitTitle: "شبه جزيرة واحدة، خمسة مواقع",
    orbitLede:
      "من المدافن النبطية في الشمال الغربي إلى القرى الحجرية في مرتفعات عسير.",
        interludeSky: "السماء نفسها فوقها جميعًا",
    interludeStone: "كل ما هنا نُحت من الأرض التي يقوم عليها",
        codaLine: "وكل ذلك ما يزال قائمًا تحت البلاد التي نشأت حوله",
        motionLabel: "الحركة",
    motionOn: "الحركة مفعّلة",
    motionOff: "الحركة متوقّفة",
    motionHint: "مقاطع مرئية وتمرير متحرّك",
    ambienceLabel: "الصوت المحيط",
    ambienceOn: "الصوت مفعّل",
    ambienceOff: "الصوت متوقّف",
    ambienceHint: "رياح ورنين حجري، مُركَّبة صوتيًّا",
    footerTagline: "معارض ثلاثية الأبعاد للتراث الثقافي، مبنيّة بطريقة حتمية.",
    footerCredits: "الاعتمادات والتراخيص",
    footerAttribution: "الصور من ويكيميديا كومنز بتراخيص المشاع الإبداعي. الإسناد يرافق الصور دائمًا.",
    footerAttributionCollection:
      "تُنسب كل صورة إلى مصوّرها أينما ظهرت، وكذلك كل ما وُلِّد منها.",
    creditsCollection: "الصور في هذه الصفحة، وكل قطعة ثلاثية الأبعاد وُلِّدت منها:",
    creditsTitle: "الاعتمادات والتراخيص",
    creditsPhotography: "كل صورة مأخوذة من ويكيميديا كومنز، ومدرجة باسم مصوّرها وترخيصها المحدد.",
    creditsReconstructions:
      "وُلِّدت عمليات إعادة البناء التاريخية من هذه الصور نفسها ومن الزاوية نفسها. " +
      "هي اجتهادات تفسيرية، وتُوسم بذلك أينما ظهرت.",
    creditsTypefaces: "الخطوط، بموجب رخصة الخطوط المفتوحة SIL 1.1:",
    creditsClose: "إغلاق الاعتمادات",
    revealThen: "إعادة بناء",
    revealNow: "صورة فوتوغرافية",
    revealCaption:
      "إعادة بناء وُلِّدت من الصورة الوثائقية ومن الزاوية نفسها. هي اجتهاد تفسيري، لا وثيقة.",
    revealSliderLabel: "قارن بين إعادة البناء والصورة",
    revealValueText: (position) =>
      position >= 50
        ? `إعادة البناء ظاهرة بنسبة ${position}٪`
        : `الصورة ظاهرة بنسبة ${100 - position}٪`,
    revealPhotographAlt: "صورة وثائقية للموقع كما هو اليوم",
    revealReconstructionAlt: "إعادة بناء للمشهد نفسه في حالته التاريخية",
    openExhibition: "افتح المعرض",
    walkOpenAir: "تجوّل في الهواء الطلق (نسخة أولية)",
    exhibitionCardMeta: (roomCount, artworkCount) =>
      `${arabicCount(roomCount, ROOM_NOUN)} · ${arabicCount(artworkCount, WORK_NOUN)}`,
    emptyExhibitions: "لا توجد معارض منشورة بعد.",
    backToExhibitions: "العودة إلى المعارض",
    notFoundTitle: "المعرض غير موجود",
    notFoundMessage: "المعرض المطلوب غير موجود أو غير منشور.",
    enter: "ادخل المتحف",
    routeAndWorks: "الجولة والأعمال",
    howToVisit: "كيف تزور",
    pauseEyebrow: "الزيارة متوقفة",
    pauseTitle: "تابع متى شئت",
    pauseMessage: "الحركة متوقفة. لم يتغير موضعك.",
    continueVisit: "متابعة الزيارة",
    fatalEyebrow: "تعذّر فتح المتحف",
    fatalTitle: "خطأ في التحميل",
    retry: "أعد المحاولة",
    consultRoute: "عرض الجولة",
    previousArtwork: "العمل السابق",
    nextArtwork: "العمل التالي",
    works: "الأعمال",
    home: "الرئيسية",
    help: "مساعدة",
    visitControls: "أدوات التحكم بالزيارة",
    joystickLabel: "تحكم لمسي بالحركة",
    viewArtwork: "عرض العمل",
    mapTitle: "مخطط المتحف",
    mapLabel: "مخطط القاعات وموضع الزائر والأعمال",
    presetLabel: "الانتقال مباشرة إلى عمل",
    presetPlaceholder: "انتقل إلى عمل…",
    presetHint: "يمكنك أيضًا استخدام مفاتيح الأرقام المتاحة",
    closeArtwork: "إغلاق معلومات العمل",
    routeEyebrow: "الجولة القيمية",
    routeTitle: "أعمال المعرض",
    closeRoute: "إغلاق الجولة",
    routeIntro: "افتح أي عمل بصيغة ميسّرة. لن يتغير موضعك.",
    helpTitle: "أدوات التحكم",
    closeHelp: "إغلاق المساعدة",
    desktopTitle: "الحاسوب",
    desktopHelp: "استخدم WASD أو الأسهم للمشي، والفأرة للنظر، وE لفحص عمل.",
    touchTitle: "الشاشة اللمسية",
    touchHelp: "استخدم أداة التحكم اليسرى للمشي، واسحب على العرض للنظر حولك.",
    tourTitle: "الجولة",
    tourHelp: "يشير السهم إلى العمل النشط. استخدم السابق أو التالي أو الأعمال.",
    playTour: "جولة إرشادية",
    stopTour: "إنهاء الجولة",
    tourStopLabel: (index, total) => `المحطة ${index} من ${total}`,
    tourStarted: "بدأت الجولة الإرشادية. تحرّك لاستعادة التحكم.",
    tourFinished: "انتهت الجولة الإرشادية. يمكنك متابعة الاستكشاف بنفسك.",
    showcaseEyebrow: "غرف يمكن التجول فيها",
    regionCodas: {
      greece: {
        line: "\u0645\u0627 \u0643\u0627\u0646 \u0647\u0646\u0627\u060c \u062a\u062e\u0645\u064a\u0646\u064b\u0627",
        note:
          "\u064a\u0643\u062a\u0645\u0644 \u0627\u0644\u0645\u0639\u0628\u062f \u0644\u0623\u0646 \u0646\u0645\u0648\u0630\u062c\u064b\u0627 \u0637\u064f\u0644\u0628 \u0645\u0646\u0647 \u0623\u0646 \u064a\u062a\u062e\u064a\u0651\u0644\u0647\u060c \u0644\u0627 \u0644\u0623\u0646 \u0623\u062d\u062f\u064b\u0627 \u0648\u062b\u0651\u0642\u0647. \u0643\u0644 \u0645\u0627 \u0639\u062f\u0627\u0647 \u0641\u064a \u0647\u0630\u0647 \u0627\u0644\u0635\u0641\u062d\u0629 \u064a\u0628\u062f\u0623 \u0645\u0646 \u0635\u0648\u0631\u0629. \u0647\u0630\u0647 \u0627\u0644\u0635\u0648\u0631\u0629 \u0627\u0644\u0648\u062d\u064a\u062f\u0629 \u0627\u0644\u062a\u064a \u062a\u0628\u062f\u0623 \u0645\u0646 \u0644\u0627 \u0634\u064a\u0621.",
      },
    },
    regionOpenings: {
      greece: {
        title: "التراث اليوناني",
        lede: "مدينتان، صوّرهما زائر واحد، وأُعيد بناؤهما من تلك الصور.",
        orbitTitle: "سالونيك وأثينا",
        orbitLede: "روتوندا رومانية في الشمال، والأكروبوليس في الجنوب، وبينهما ما يملأ متحفًا من القطع.",
      },
    },
    collectionEyebrow: "سالونيك وأثينا",
    collectionTitle: "اليونان، في ثلاث مرات",
    collectionIntro: "سبع عشرة صورة فوتوغرافية، وكل ما هو ثلاثي الأبعاد في هذه الصفحة مُولَّد منها. لا شيء هنا مسح ضوئي لموقع، ولا شيء منه لنا.",
    collectionCreditLead: "تصوير",
    collectionCreditOne: "تصوير",
    collectionPlatesTitle: "الصور",
    collectionObjectsTitle: "القطع، وقد أُديرت",
    collectionObjectsIntro: "كل قطعة مُولَّدة من الصورة الواحدة المجاورة لها. اسحبها لتدور، وقارن بين النصفين.",
    collectionGenerated: "مُولَّد من صورة التقطها",
    collectionTurn: "أدرها",
    showcaseRoomsGenerated: "مُولَّد من صور التقطها",
    showcaseTitle: "قاعات ممسوحة ضوئيًا",
    showcaseTitleGenerated: "غرف أُعيد بناؤها من الصور",
    showcaseIntroGenerated:
      "سحابات غاوسية وُلِّدت من الصور أعلاه، ويمكن التجول فيها داخل المتصفح. تفتح كل قاعة عند الوصول إليها — اسحب للنظر، واستخدم W A S D للحركة.",
    showcaseIntro:
      "فضاءات حقيقية التُقطت بتقنية الsplats الغاوسية ويمكن التجوّل فيها داخل المتصفح. تفتح كل قاعة عند الوصول إليها؛ اسحب للنظر، واستخدم W A S D للحركة.",
    showcaseOpen: "ادخل القاعة",
    showcaseLoading: "جارٍ فتح العرض ثلاثي الأبعاد…",
    showcaseOwnCredit: "سِمام ديجيتال",
    showcaseSampleNotice:
      "هذه اللقطات من عمل آخرين، وتُعرض فقط لتوضيح الشكل. أما العمل النهائي فسيعتمد على مسح ضوئي لموقع المتحف نفسه.",
    showcaseCreditLink: "اسم المُنفِّذ مذكور داخل العارض",
    showcaseArchiveTitle: "قاعات مصمّمة خصيصًا",
    author: "المصوّر",
    century: "القرن",
    styles: "الطُّرز",
    deities: "الآلهة",
    mortals: "الشخصيات",
    museum: "الموقع",
    ready: (rooms, artworks) =>
      `المتحف جاهز: ${arabicCount(rooms, ROOM_NOUN)} و${arabicCount(artworks, WORK_NOUN)}.`,
    corridor: "ممر بين القاعات",
    routeCompleted: "اكتملت الجولة",
    loadPhases: {
      manifest: "جارٍ التحقق من البيان…",
      report: "جارٍ فحص تقرير بلندر…",
      content: "جارٍ تحميل الجولة والنصوص…",
      scene: "جارٍ التحقق من المشهد ثلاثي الأبعاد…",
      validated: "جارٍ تجهيز الكاميرا والتصادم والتفاعل…",
    },
    artworkKicker: (order, total, room) => `العمل ${order} من ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}، تصوير ${author}`,
    viewFullscreen: (title, author) => `عرض ${title}، تصوير ${author}، بملء الشاشة`,
    exitFullscreen: (title) => `الخروج من عرض ${title} بملء الشاشة`,
    towards: (title) => `نحو ${title}`,
    towardsPassage: "نحو الممر",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `بدأت الزيارة. العمل النشط: ${title}.`,
    roomChanged: (room) => `الفضاء الحالي: ${room}.`,
    activeArtwork: (order, total, title) => `العمل النشط ${order} من ${total}: ${title}.`,
    completedWith: (title) => `اكتملت الجولة بـ${title}.`,
    navigationPosition: (title) => `موضع المشاهدة: ${title}. العمل أمامك.`,
    resetAnnouncement: "عدت إلى مدخل المتحف.",
    pointerLockFailure: "لم يمنح المتصفح التحكم بالفأرة. حاول مرة أخرى.",
    invalidLink: "رابط العمل غير صالح وقد جرى تجاهله.",
    unknownArtwork: "العمل المطلوب لا ينتمي إلى هذا المعرض.",
    defaultPause: "الحركة متوقفة. لم يتغير موضعك.",
    webglFailure: "هذا المتصفح لا يدعم WebGL2. تتطلب الزيارة ثلاثية الأبعاد متصفحًا متوافقًا.",
    contextLoss: "فقد المتصفح سياق الرسوميات. أعد التحميل لإعادة بناء الزيارة.",
    identityFailure: "الملفات المنشورة لا تنتمي إلى البناء نفسه.",
    missingAsset: "أحد الملفات المولّدة مفقود. أعد نشر الموارد.",
    genericFailure: "تعذّر التحقق الكامل من المشهد.",
  },
  zh: {
    locale: "zh-Hans",
    direction: "ltr",
    languageLabel: "语言",
    regionLabel: "文化遗产",
    regionSoon: "筹备中",
    regionNames: {
      "saudi-arabia": "沙特阿拉伯",
      "united-arab-emirates": "阿拉伯联合酋长国",
      italy: "意大利",
      greece: "希腊",
      "united-kingdom": "英国",
      ireland: "爱尔兰",
      france: "法国",
    },
    pageDescription: "沙特文化遗产的可漫游三维展览。",
    canvasLabel: "博物馆三维视图。参观路线同时提供列表形式。",
    museumEyebrow: "虚拟博物馆",
    fallbackTitle: "虚拟博物馆",
    loadingThesis: "正在载入策展叙述…",
    preparing: "正在准备展览数据…",
    exhibitionsTitle: "展览",
    exhibitionsIntro: "选择一个展览开始参观。",
    storyEyebrow: "沙特文化遗产",
    storyLead:
      "五处遗址，从纳巴泰人的南方直到阿西尔群山。每一处都重建为可以步行穿越的展厅，" +
      "配以纪实摄影与阿拉伯语、英语的文字。",
    storyScrollCue: "向下滚动，逐一走过",
    storyIndexTitle: "五个展览",
    // Chinese counts with a measure word and needs no plural form.
    storyChapterLabel: (index, total) => `第 ${index} / ${total} 章`,
        openingTitle: "沙特阿拉伯遗产",
    openingLede: "五处联合国教科文组织遗产地，重建为可以走进去的展厅。",
    orbitTitle: "一个半岛，五处遗产",
    orbitLede: "从西北的纳巴泰石墓，到阿西尔高原的石砌村落。",
        interludeSky: "同一片星空，覆盖着它们",
    interludeStone: "这里的一切，都取自它所立足的土地",
        codaLine: "这一切，依然矗立在后来生长起来的国度之下",
        motionLabel: "动效",
    motionOn: "动效已开启",
    motionOff: "动效已关闭",
    motionHint: "视频与滚动动画",
    ambienceLabel: "环境音",
    ambienceOn: "环境音已开启",
    ambienceOff: "环境音已关闭",
    ambienceHint: "合成的风声与石室共鸣",
    footerTagline: "文化遗产的三维展览，以确定性流程构建。",
    footerCredits: "版权与许可",
    footerAttribution: "照片来自维基共享资源，依据知识共享许可协议使用。署名信息始终随图片一同呈现。",
    footerAttributionCollection: "每张照片在出现之处均标注摄影者，由其生成的一切内容同样如此。",
    creditsCollection: "本页的照片，以及由这些照片生成的每一件三维作品：",
    creditsTitle: "版权与许可",
    creditsPhotography: "每张照片均来自维基共享资源，并标注其摄影者与具体许可协议。",
    creditsReconstructions:
      "历史复原图由这些照片、从同一视角生成。它们是推断，凡出现之处均标注为复原图。",
    creditsTypefaces: "字体，依据 SIL 开放字体许可协议 1.1：",
    creditsClose: "关闭版权信息",
    revealThen: "复原图",
    revealNow: "实景照片",
    revealCaption: "复原图以纪实照片为依据、从同一视角生成。它是一种推断，而非记录。",
    revealSliderLabel: "对比复原图与实景照片",
    revealValueText: (position) =>
      position >= 50 ? `复原图显示 ${position}%` : `实景照片显示 ${100 - position}%`,
    revealPhotographAlt: "该遗址今日面貌的纪实照片",
    revealReconstructionAlt: "同一视角下遗址历史面貌的复原图",
    openExhibition: "进入展览",
    walkOpenAir: "在露天中漫步（预览）",
    exhibitionCardMeta: (roomCount, artworkCount) => `${roomCount} 个展厅 · ${artworkCount} 件作品`,
    emptyExhibitions: "目前还没有已发布的展览。",
    backToExhibitions: "返回展览列表",
    notFoundTitle: "未找到展览",
    notFoundMessage: "所请求的展览不存在，或尚未发布。",
    enter: "进入博物馆",
    routeAndWorks: "路线与作品",
    howToVisit: "参观方式",
    pauseEyebrow: "参观已暂停",
    pauseTitle: "随时可以继续",
    pauseMessage: "移动已暂停，您的位置没有改变。",
    continueVisit: "继续参观",
    fatalEyebrow: "无法打开博物馆",
    fatalTitle: "载入错误",
    retry: "重试",
    consultRoute: "查看路线",
    previousArtwork: "上一件作品",
    nextArtwork: "下一件作品",
    works: "作品",
    home: "首页",
    help: "帮助",
    visitControls: "参观控制",
    joystickLabel: "触屏移动控制",
    viewArtwork: "查看作品",
    mapTitle: "博物馆平面图",
    mapLabel: "展厅平面、参观者位置与作品分布",
    presetLabel: "直接前往某件作品",
    presetPlaceholder: "前往作品…",
    presetHint: "也可以使用可用的数字键",
    closeArtwork: "关闭作品信息",
    routeEyebrow: "策展路线",
    routeTitle: "展览作品",
    closeRoute: "关闭路线",
    routeIntro: "以无障碍形式打开任意作品，您的位置不会改变。",
    helpTitle: "操作说明",
    closeHelp: "关闭帮助",
    desktopTitle: "电脑",
    desktopHelp: "用 WASD 或方向键行走，用鼠标环视，对准作品按 E 键查看。",
    touchTitle: "触摸屏",
    touchHelp: "用左侧摇杆行走，在画面上拖动以环视。",
    tourTitle: "路线",
    tourHelp: "箭头指向当前作品。可使用「上一件」「下一件」或「作品」。",
    playTour: "导览",
    stopTour: "退出导览",
    tourStopLabel: (index, total) => `第 ${index} 站，共 ${total} 站`,
    tourStarted: "导览已开始。移动即可收回控制权。",
    tourFinished: "导览已结束。您可以继续自行参观。",
    showcaseEyebrow: "可漫步的空间",
    regionCodas: {
      greece: {
        line: "\u6b64\u5904\u66fe\u6709\u4e4b\u7269\uff0c\u51fa\u4e8e\u63a8\u60f3",
        note:
          "\u795e\u5e99\u4e4b\u6240\u4ee5\u91cd\u65b0\u5b8c\u6574\uff0c\u662f\u56e0\u4e3a\u6a21\u578b\u88ab\u8981\u6c42\u53bb\u60f3\u8c61\u5b83\uff0c\u800c\u4e0d\u662f\u56e0\u4e3a\u6709\u4eba\u8bb0\u5f55\u8fc7\u5b83\u3002\u672c\u9875\u5176\u4f59\u7684\u4e00\u5207\u90fd\u59cb\u4e8e\u4e00\u5f20\u7167\u7247\uff1b\u53ea\u6709\u8fd9\u4e00\u5e45\u59cb\u4e8e\u865a\u65e0\u3002",
      },
    },
    regionOpenings: {
      greece: {
        title: "希腊遗产",
        lede: "两座城市，由一位访客拍摄，并从这些照片中重建。",
        orbitTitle: "塞萨洛尼基与雅典",
        orbitLede: "北有罗马圆形大厅，南有卫城，其间是足以填满一座博物馆的器物。",
      },
    },
    collectionEyebrow: "塞萨洛尼基与雅典",
    collectionTitle: "希腊，三重视角",
    collectionIntro: "十七张照片，以及本页所有由它们生成的三维内容。这里没有任何一件是实地扫描，也没有一件属于我们。",
    collectionCreditLead: "摄影",
    collectionCreditOne: "摄影",
    collectionPlatesTitle: "照片",
    collectionObjectsTitle: "可转动的器物",
    collectionObjectsIntro: "每一件都由旁边那一张照片生成。拖动可以转动它，并与左边的照片对照。",
    collectionGenerated: "生成自一张照片，摄影",
    collectionTurn: "转动它",
    showcaseRoomsGenerated: "生成自照片，摄影",
    showcaseTitle: "扫描展厅",
    showcaseTitleGenerated: "由照片重建的空间",
    showcaseIntroGenerated: "由上方照片生成的高斯泼溅，可在浏览器中漫步。每个展厅在你滚到时自行打开——拖动视角，W A S D 移动。",
    showcaseIntro: "以高斯泼溅技术采集的真实空间，可在浏览器中自由行走。每个展厅在您滚到时自行打开；拖动视角，W A S D 移动。",
    showcaseOpen: "进入展厅",
    showcaseLoading: "正在打开 3D 视图…",
    showcaseOwnCredit: "Simam Digital",
    showcaseSampleNotice: "以下场景为他人作品，仅用于展示形式。正式版本将采用博物馆自有场地的实地扫描。",
    showcaseCreditLink: "作者信息见查看器内",
    showcaseArchiveTitle: "定制建造的展厅",
    author: "作者",
    century: "世纪",
    styles: "风格",
    deities: "神祇",
    mortals: "凡人",
    museum: "博物馆",
    ready: (rooms, artworks) => `博物馆已就绪：${rooms} 个展厅，${artworks} 件作品。`,
    corridor: "展厅之间的通道",
    routeCompleted: "路线已走完",
    loadPhases: {
      manifest: "正在校验清单…",
      report: "正在核对 Blender 报告…",
      content: "正在载入路线与评述…",
      scene: "正在验证三维场景…",
      validated: "正在准备镜头、碰撞与交互…",
    },
    artworkKicker: (order, total, room) => `作品 ${order} / ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}，${author} 作`,
    viewFullscreen: (title, author) => `全屏查看 ${author} 的《${title}》`,
    exitFullscreen: (title) => `退出《${title}》的全屏视图`,
    towards: (title) => `前往《${title}》`,
    towardsPassage: "前往通道",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `参观已开始。当前作品：${title}。`,
    roomChanged: (room) => `当前空间：${room}。`,
    activeArtwork: (order, total, title) => `当前作品 ${order} / ${total}：${title}。`,
    completedWith: (title) => `路线已在《${title}》结束。`,
    navigationPosition: (title) => `观看位置：${title}。作品就在您正前方。`,
    resetAnnouncement: "您已回到博物馆入口。",
    pointerLockFailure: "浏览器未授予鼠标控制权限，请重试。",
    invalidLink: "作品链接无效，已忽略。",
    unknownArtwork: "所请求的作品不属于本展览。",
    defaultPause: "移动已暂停，您的位置没有改变。",
    webglFailure: "此浏览器不支持 WebGL2。三维参观需要兼容的浏览器。",
    contextLoss: "浏览器丢失了图形上下文。请重新载入以重建参观。",
    identityFailure: "已发布的文件不属于同一次构建。",
    missingAsset: "缺少一个生成的文件，请重新发布资源。",
    genericFailure: "场景未能完成全部校验。",
  },
  hi: {
    locale: "hi",
    direction: "ltr",
    languageLabel: "भाषा",
    regionLabel: "विरासत",
    regionSoon: "तैयारी में",
    regionNames: {
      "saudi-arabia": "सऊदी अरब",
      "united-arab-emirates": "संयुक्त अरब अमीरात",
      italy: "इटली",
      greece: "ग्रीस",
      "united-kingdom": "यूनाइटेड किंगडम",
      ireland: "आयरलैंड",
      france: "फ़्रान्स",
    },
    pageDescription: "सऊदी विरासत की त्रि-आयामी प्रदर्शनियाँ, जिनमें आप चल-फिर सकते हैं।",
    canvasLabel: "संग्रहालय का त्रि-आयामी दृश्य। भ्रमण सूची के रूप में भी उपलब्ध है।",
    museumEyebrow: "आभासी संग्रहालय",
    fallbackTitle: "आभासी संग्रहालय",
    loadingThesis: "क्यूरेटोरियल विवरण लोड हो रहा है…",
    preparing: "प्रदर्शनी की सामग्री तैयार की जा रही है…",
    exhibitionsTitle: "प्रदर्शनियाँ",
    exhibitionsIntro: "भ्रमण शुरू करने के लिए कोई प्रदर्शनी चुनें।",
    storyEyebrow: "सऊदी विरासत",
    storyLead:
      "पाँच स्थल, नबातियों के दक्षिण से लेकर असीर के पहाड़ों तक। हर एक को ऐसी दीर्घा " +
      "के रूप में पुनर्निर्मित किया गया है जिसमें आप चलकर घूम सकते हैं — साथ में " +
      "दस्तावेज़ी छायाचित्र और अरबी तथा अंग्रेज़ी में लेखन।",
    storyScrollCue: "स्क्रॉल करके इनमें घूमिए",
    storyIndexTitle: "पाँचों प्रदर्शनियाँ",
    storyChapterLabel: (index, total) => `${total} में से ${index}`,
        openingTitle: "सऊदी अरब की विरासत",
    openingLede:
      "यूनेस्को में दर्ज पाँच स्थल, ऐसी दीर्घाओं के रूप में पुनर्निर्मित जिनमें आप चल सकते हैं।",
    orbitTitle: "एक प्रायद्वीप, पाँच स्थल",
    orbitLede:
      "उत्तर-पश्चिम की नबातियन कब्रों से लेकर असीर की पहाड़ी पत्थर-बस्तियों तक।",
        interludeSky: "इन सब पर वही आकाश",
    interludeStone: "यहाँ का सब कुछ उसी ज़मीन से तराशा गया है",
        codaLine: "और यह सब उस देश के नीचे आज भी खड़ा है जो इसके चारों ओर उगा",
        motionLabel: "गति",
    motionOn: "गति चालू",
    motionOff: "गति बंद",
    motionHint: "वीडियो और सजीव स्क्रॉलिंग",
    ambienceLabel: "परिवेशी ध्वनि",
    ambienceOn: "ध्वनि चालू",
    ambienceOff: "ध्वनि बंद",
    ambienceHint: "संश्लेषित पवन और पाषाण-अनुनाद",
    footerTagline: "सांस्कृतिक विरासत की त्रि-आयामी प्रदर्शनियाँ, नियतात्मक रूप से निर्मित।",
    footerCredits: "श्रेय और अनुज्ञप्तियाँ",
    footerAttribution:
      "छायाचित्र विकिमीडिया कॉमन्स से, क्रिएटिव कॉमन्स अनुज्ञप्तियों के अंतर्गत। " +
      "श्रेय सदैव छवियों के साथ रहता है।",
    footerAttributionCollection:
      "प्रत्येक तस्वीर का श्रेय जहाँ भी वह दिखे उसके छायाकार को दिया जाता है, और उससे बनी हर वस्तु का भी।",
    creditsCollection: "इस पृष्ठ की तस्वीरें, और उनसे बनी हर त्रि-आयामी कृति:",
    creditsTitle: "श्रेय और अनुज्ञप्तियाँ",
    creditsPhotography:
      "प्रत्येक छायाचित्र विकिमीडिया कॉमन्स से है, और अपने छायाकार तथा विशिष्ट " +
      "अनुज्ञप्ति के साथ सूचीबद्ध है।",
    creditsReconstructions:
      "ऐतिहासिक पुनर्निर्माण इन्हीं छायाचित्रों से, उसी दृष्टिकोण से बनाए गए हैं। " +
      "ये व्याख्याएँ हैं, और जहाँ भी दिखें वहाँ ऐसा ही अंकित रहता है।",
    creditsTypefaces: "टाइपफ़ेस, SIL ओपन फ़ॉन्ट लाइसेंस 1.1 के अंतर्गत:",
    creditsClose: "श्रेय बंद करें",
    revealThen: "पुनर्निर्माण",
    revealNow: "छायाचित्र",
    revealCaption:
      "यह पुनर्निर्माण उसी दृष्टिकोण से, दस्तावेज़ी छायाचित्र के आधार पर बनाया गया है। " +
      "यह एक व्याख्या है, अभिलेख नहीं।",
    revealSliderLabel: "पुनर्निर्माण की छायाचित्र से तुलना करें",
    revealValueText: (position) =>
      position >= 50
        ? `पुनर्निर्माण ${position}% दिख रहा है`
        : `छायाचित्र ${100 - position}% दिख रहा है`,
    revealPhotographAlt: "स्थल का आज का दस्तावेज़ी छायाचित्र",
    revealReconstructionAlt: "उसी दृश्य का ऐतिहासिक अवस्था में पुनर्निर्माण",
    openExhibition: "प्रदर्शनी खोलें",
    walkOpenAir: "खुली हवा में टहलें (झलक)",
    exhibitionCardMeta: (roomCount, artworkCount) =>
      `${roomCount} ${roomCount === 1 ? "कक्ष" : "कक्ष"} · ${artworkCount} ${artworkCount === 1 ? "कृति" : "कृतियाँ"}`,
    emptyExhibitions: "अभी तक कोई प्रदर्शनी प्रकाशित नहीं हुई है।",
    backToExhibitions: "प्रदर्शनियों पर लौटें",
    notFoundTitle: "प्रदर्शनी नहीं मिली",
    notFoundMessage: "माँगी गई प्रदर्शनी मौजूद नहीं है या प्रकाशित नहीं है।",
    enter: "संग्रहालय में प्रवेश करें",
    routeAndWorks: "भ्रमण और कृतियाँ",
    howToVisit: "भ्रमण कैसे करें",
    pauseEyebrow: "भ्रमण रुका हुआ है",
    pauseTitle: "जब चाहें, आगे बढ़ें",
    pauseMessage: "गति रुकी हुई है। आपकी स्थिति नहीं बदली है।",
    continueVisit: "भ्रमण जारी रखें",
    fatalEyebrow: "संग्रहालय नहीं खुल सका",
    fatalTitle: "लोडिंग त्रुटि",
    retry: "फिर से कोशिश करें",
    consultRoute: "भ्रमण देखें",
    previousArtwork: "पिछली कृति",
    nextArtwork: "अगली कृति",
    works: "कृतियाँ",
    home: "मुख्य पृष्ठ",
    help: "सहायता",
    visitControls: "भ्रमण नियंत्रण",
    joystickLabel: "स्पर्श से चलने का नियंत्रण",
    viewArtwork: "कृति देखें",
    mapTitle: "संग्रहालय का नक्शा",
    mapLabel: "कक्षों का नक्शा, आगंतुक की स्थिति और कृतियाँ",
    presetLabel: "सीधे किसी कृति पर जाएँ",
    presetPlaceholder: "किसी कृति पर जाएँ…",
    presetHint: "आप उपलब्ध अंक कुंजियों का भी उपयोग कर सकते हैं",
    closeArtwork: "कृति की जानकारी बंद करें",
    routeEyebrow: "क्यूरेटोरियल भ्रमण",
    routeTitle: "प्रदर्शनी की कृतियाँ",
    closeRoute: "भ्रमण बंद करें",
    routeIntro: "किसी भी कृति को सुलभ रूप में खोलें। आपकी स्थिति नहीं बदलेगी।",
    helpTitle: "नियंत्रण",
    closeHelp: "सहायता बंद करें",
    desktopTitle: "डेस्कटॉप",
    desktopHelp: "चलने के लिए WASD या तीर, देखने के लिए माउस, और कृति पर E दबाएँ।",
    touchTitle: "स्पर्श स्क्रीन",
    touchHelp: "चलने के लिए बाएँ नियंत्रण का उपयोग करें और देखने के लिए दृश्य पर उँगली घुमाएँ।",
    tourTitle: "भ्रमण",
    tourHelp: "तीर सक्रिय कृति की ओर संकेत करता है। पिछली, अगली या कृतियाँ का उपयोग करें।",
    playTour: "निर्देशित भ्रमण",
    stopTour: "भ्रमण छोड़ें",
    tourStopLabel: (index, total) => `पड़ाव ${index} / ${total}`,
    tourStarted: "निर्देशित भ्रमण शुरू हुआ। नियंत्रण वापस लेने के लिए चलें।",
    tourFinished: "निर्देशित भ्रमण समाप्त हुआ। आप स्वयं देखना जारी रख सकते हैं।",
    showcaseEyebrow: "जिन कमरों में आप चल सकते हैं",
    regionCodas: {
      greece: {
        line: "\u092f\u0939\u093e\u0901 \u091c\u094b \u0916\u0921\u093c\u093e \u0925\u093e, \u0905\u0928\u0941\u092e\u093e\u0928 \u0938\u0947",
        note:
          "\u092e\u0902\u0926\u093f\u0930 \u0907\u0938\u0932\u093f\u090f \u092a\u0942\u0930\u093e \u0939\u094b\u0924\u093e \u0939\u0948 \u0915\u094d\u092f\u094b\u0902\u0915\u093f \u090f\u0915 \u092e\u0949\u0921\u0932 \u0938\u0947 \u0909\u0938\u0915\u0940 \u0915\u0932\u094d\u092a\u0928\u093e \u0915\u0930\u0928\u0947 \u0915\u094b \u0915\u0939\u093e \u0917\u092f\u093e, \u0907\u0938\u0932\u093f\u090f \u0928\u0939\u0940\u0902 \u0915\u093f \u0915\u093f\u0938\u0940 \u0928\u0947 \u0909\u0938\u0947 \u0926\u0930\u094d\u091c \u0915\u093f\u092f\u093e\u0964 \u0907\u0938 \u092a\u0943\u0937\u094d\u0920 \u0915\u0940 \u092c\u093e\u0915\u0940 \u0939\u0930 \u091a\u0940\u091c\u093c \u090f\u0915 \u0924\u0938\u094d\u0935\u0940\u0930 \u0938\u0947 \u0936\u0941\u0930\u0942 \u0939\u094b\u0924\u0940 \u0939\u0948\u0964 \u0915\u0947\u0935\u0932 \u092f\u0939\u0940 \u091b\u0935\u093f \u0936\u0942\u0928\u094d\u092f \u0938\u0947 \u0936\u0941\u0930\u0942 \u0939\u094b\u0924\u0940 \u0939\u0948\u0964",
      },
    },
    regionOpenings: {
      greece: {
        title: "यूनानी विरासत",
        lede: "दो शहर, एक यात्री द्वारा खींचे गए, और उन्हीं तस्वीरों से फिर से रचे गए।",
        orbitTitle: "थेसालोनिकी और एथेंस",
        orbitLede: "उत्तर में एक रोमन रोटुंडा, दक्षिण में एक्रोपोलिस, और बीच में एक संग्रहालय भर की वस्तुएँ।",
      },
    },
    collectionEyebrow: "थेसालोनिकी और एथेंस",
    collectionTitle: "ग्रीस, तीन परतों में",
    collectionIntro: "सत्रह तस्वीरें, और इस पृष्ठ की हर त्रि-आयामी चीज़ उन्हीं से बनाई गई। यहाँ कुछ भी किसी स्थल का स्कैन नहीं है, और कुछ भी हमारा नहीं है।",
    collectionCreditLead: "छायाचित्र",
    collectionCreditOne: "छायाचित्र",
    collectionPlatesTitle: "तस्वीरें",
    collectionObjectsTitle: "वस्तुएँ, घुमाकर",
    collectionObjectsIntro: "इनमें से हर एक बगल की उसी एक तस्वीर से बनाई गई है। घुमाने के लिए खींचें, और दोनों हिस्सों की तुलना करें।",
    collectionGenerated: "एक तस्वीर से बनाया गया, छायाचित्र",
    collectionTurn: "घुमाएँ",
    showcaseRoomsGenerated: "तस्वीरों से बनाया गया, छायाचित्र",
    showcaseTitle: "स्कैन किए गए कक्ष",
    showcaseTitleGenerated: "तस्वीरों से पुनर्निर्मित कक्ष",
    showcaseIntroGenerated:
      "ऊपर की तस्वीरों से बनाए गए गॉसियन स्प्लैट, जिनमें आप ब्राउज़र में चल सकते हैं। हर कक्ष वहाँ पहुँचते ही खुल जाता है — देखने के लिए खींचें, चलने के लिए W A S D।",
    showcaseIntro:
      "गॉसियन स्प्लैट के रूप में कैद असली स्थान, जिनमें ब्राउज़र के भीतर ही घूमा जा सकता है। हर कक्ष वहाँ पहुँचते ही खुल जाता है; देखने के लिए खींचें, चलने के लिए W A S D।",
    showcaseOpen: "भीतर जाएँ",
    showcaseLoading: "3D दृश्य खोला जा रहा है…",
    showcaseOwnCredit: "सिमाम डिजिटल",
    showcaseSampleNotice:
      "ये कैप्चर अन्य लोगों का कार्य हैं और केवल प्रारूप दिखाने के लिए प्रस्तुत हैं। अंतिम रचना में संग्रहालय के अपने स्थल का स्कैन प्रयोग होगा।",
    showcaseCreditLink: "निर्माता का श्रेय व्यूअर में दिया गया है",
    showcaseArchiveTitle: "विशेष रूप से बनाए गए कक्ष",
    author: "कलाकार",
    century: "शताब्दी",
    styles: "शैलियाँ",
    deities: "देवता",
    mortals: "मनुष्य",
    museum: "संग्रहालय",
    ready: (rooms, artworks) => `संग्रहालय तैयार है: ${rooms} कक्ष और ${artworks} कृतियाँ।`,
    corridor: "कक्षों के बीच का रास्ता",
    routeCompleted: "भ्रमण पूरा हुआ",
    loadPhases: {
      manifest: "मैनिफ़ेस्ट की जाँच हो रही है…",
      report: "Blender रिपोर्ट की जाँच हो रही है…",
      content: "भ्रमण और समीक्षाएँ लोड हो रही हैं…",
      scene: "त्रि-आयामी दृश्य सत्यापित हो रहा है…",
      validated: "कैमरा, टकराव और परस्पर क्रिया तैयार की जा रही है…",
    },
    artworkKicker: (order, total, room) => `कृति ${order} / ${total} · ${room}`,
    artworkImageLabel: (title, author) => `${title}, ${author} द्वारा`,
    viewFullscreen: (title, author) => `${author} की ${title} को पूर्ण स्क्रीन में देखें`,
    exitFullscreen: (title) => `${title} का पूर्ण-स्क्रीन दृश्य बंद करें`,
    towards: (title) => `${title} की ओर`,
    towardsPassage: "रास्ते की ओर",
    selection: (title) => `E · ${title}`,
    visitStarted: (title) => `भ्रमण शुरू हुआ। सक्रिय कृति: ${title}।`,
    roomChanged: (room) => `वर्तमान स्थान: ${room}।`,
    activeArtwork: (order, total, title) => `सक्रिय कृति ${order} / ${total}: ${title}।`,
    completedWith: (title) => `भ्रमण ${title} के साथ पूरा हुआ।`,
    navigationPosition: (title) => `देखने की स्थिति: ${title}। कृति आपके सामने है।`,
    resetAnnouncement: "आप संग्रहालय के प्रवेश द्वार पर लौट आए हैं।",
    pointerLockFailure: "ब्राउज़र ने माउस नियंत्रण नहीं दिया। फिर से कोशिश करें।",
    invalidLink: "कृति का लिंक अमान्य था और उसे छोड़ दिया गया है।",
    unknownArtwork: "माँगी गई कृति इस प्रदर्शनी की नहीं है।",
    defaultPause: "गति रुकी हुई है। आपकी स्थिति नहीं बदली है।",
    webglFailure: "यह ब्राउज़र WebGL2 का समर्थन नहीं करता। त्रि-आयामी भ्रमण के लिए संगत ब्राउज़र चाहिए।",
    contextLoss: "ब्राउज़र का ग्राफ़िक्स संदर्भ खो गया। भ्रमण फिर से बनाने के लिए पृष्ठ पुनः लोड करें।",
    identityFailure: "प्रकाशित फ़ाइलें एक ही बिल्ड की नहीं हैं।",
    missingAsset: "एक जनित फ़ाइल अनुपस्थित है। संसाधन फिर से प्रकाशित करें।",
    genericFailure: "दृश्य की पूरी जाँच नहीं हो सकी।",
  },
};

/**
 * The opening lines for a heritage, falling back to the ones the site started with.
 *
 * Saudi Arabia's copy predates regions existing, so it stays where it is rather than
 * being migrated into the map for symmetry: moving it would touch five languages to
 * change nothing a visitor can see.
 */
export function codaFor(copy: UiCopy, region: RegionId): RegionCoda | null {
  return copy.regionCodas[region] ?? null;
}

export function openingFor(copy: UiCopy, region: RegionId): RegionOpening {
  return (
    copy.regionOpenings[region] ?? {
      title: copy.openingTitle,
      lede: copy.openingLede,
      orbitTitle: copy.orbitTitle,
      orbitLede: copy.orbitLede,
    }
  );
}
